import type { TimestampStyle } from '@semeris/workspace-ui/chat-config';
import { format, toZonedTime } from 'date-fns-tz';

import { DateFormat } from '../models/common/dateformat.enum';

/** Clock time. `/settings/profile` lets the user choose a date format, never a time one. */
const TIME_PATTERN = 'HH:mm';

type DateStyle = Exclude<TimestampStyle, 'time' | 'datetime'>;

/**
 * One date-fns pattern per profile format × the two axes a `date*` style names
 * (`short` month, `noyear`). The `date` column is the profile's own pattern,
 * zero-padded day included, so nothing already rendered changes; the narrower
 * forms use an unpadded day, because `08 Aug` in a sidebar badge reads as a
 * code. ISO ignores both axes — a bare `08-31` is not recognisable as a date.
 */
const DATE_PATTERNS: Record<DateFormat, Record<DateStyle, string>> = {
  [DateFormat.ISO]: {
    date: 'yyyy-MM-dd',
    'date-noyear': 'yyyy-MM-dd',
    'date-short': 'yyyy-MM-dd',
    'date-short-noyear': 'yyyy-MM-dd',
  },
  [DateFormat.UK]: {
    date: 'dd MMMM yyyy',
    'date-noyear': 'd MMMM',
    'date-short': 'd MMM yyyy',
    'date-short-noyear': 'd MMM',
  },
  [DateFormat.US]: {
    date: 'MMMM dd, yyyy',
    'date-noyear': 'MMMM d',
    'date-short': 'MMM d, yyyy',
    'date-short-noyear': 'MMM d',
  },
};

function isDateFormat(pattern: string): pattern is DateFormat {
  return pattern in DATE_PATTERNS;
}

/**
 * Render an ISO timestamp for **display**, using the two preferences the user
 * actually sets on `/settings/profile` — **Date format** and **Time zone**.
 * The profile fixes the day-month order; the chat says per surface how long
 * the month is and whether the year is wanted (`style`).
 *
 * Display only: never group, compare or sort on the result. The user's format
 * is not sortable, and grouping on rendered text is what put two identically
 * labelled headings in the chat sidebar. `calendarDay` in
 * `@semeris/workspace-ui/chat` is the grouping key.
 *
 * @param dateFormat the user's date pattern (`UserProfileSelectors.dateFormat`,
 *   already mapped through `dateFormatByOptions`) — a row of `DATE_PATTERNS`.
 *   Falls back to ISO while the profile is still loading, or for a pattern the
 *   table does not know.
 * @param timeZone the user's IANA zone (`UserProfileSelectors.timezone`).
 * @returns the rendered stamp, or `''` if the zone is unusable.
 */
export function formatUserTimestamp(iso: string, style: TimestampStyle, dateFormat: string | undefined, timeZone: string): string {
  const patterns = DATE_PATTERNS[dateFormat && isDateFormat(dateFormat) ? dateFormat : DateFormat.ISO];
  const pattern = style === 'time' ? TIME_PATTERN : style === 'datetime' ? `${patterns.date} ${TIME_PATTERN}` : patterns[style];
  try {
    return format(toZonedTime(iso, timeZone), pattern, { timeZone });
  } catch (err) {
    // A stale profile zone makes Intl throw, and callers render this inside a
    // computed — unguarded it would take out the whole sidebar, not one stamp.
    console.error(err);
    return '';
  }
}
